<template>
  <div class="freebets-round text-black md:px-3 animate-fade-in">
    <div>
      <Filters
        :options="options"
        :showActionButton="true"
        :search="search"
        :resetFilter="resetFilter"
        :resetPagination="resetPagination"
        class="bg-transparent p-0"
      />

      <div
        v-if="isLoading"
        class="flex-grow flex flex-col items-center justify-center p-20"
      >
        <Loader />
      </div>

      <div
        v-else
        class="table-container-custom shadow-sm rounded-2xl overflow-hidden border border-1 mb-3 mt-3"
      >
        <DataTable
          :value="tableData"
          :lazy="true"
          :paginator="true"
          :rows="selectedLimit"
          :totalRecords="totalElements"
          :loading="isLoading"
          :first="(currentPage - 1) * selectedLimit"
          @page="onPage"
          dataKey="_id"
          removableSort
          stripedRows
          scrollable
          scrollHeight="flex"
          responsiveLayout="stack"
          :rowsPerPageOptions="[15, 25, 50, 100]"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          :currentPageReportTemplate="`Showing {first} to {last} of {totalRecords} campaigns`"
          showCurrentPageReport
        >
          <template #empty>
            <div class="text-center p-8 text-gray-500">
              No players found for this campaign.
            </div>
          </template>

          <Column
            v-for="col in activeColumns"
            :key="col.slug"
            :field="col.slug"
            :header="col.label || col.slug.toUpperCase()"
          >
            <template #body="slotProps" v-if="col.type === 'date'">
              <span class="text-sm text-gray-600">
                {{ formatDate(slotProps.data[col.slug]) }}
              </span>
            </template>

            <template #body="slotProps" v-else-if="col.type === 'boolean'">
              <span
                :class="
                  slotProps.data[col.slug]
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                "
                class="inline-flex items-center gap-1.5 w-8 h-8 flex items-center justify-center px-2.5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wide"
              >
                <i
                  :class="
                    slotProps.data[col.slug]
                      ? 'fa-solid fa-check'
                      : 'fa-solid fa-xmark'
                  "
                  class="text-[10px]"
                ></i>
              </span>
            </template>

            <template #body="slotProps" v-else-if="col.type === 'number'">
              <span class="font-bold">
                {{ slotProps.data[col.slug] }}
              </span>
            </template>

            <template
              #body="slotProps"
              v-else-if="col.type === 'action_button'"
            >
              <div class="flex justify-start">
                <Button
                  icon="pi pi-trash"
                  rounded
                  text
                  severity="danger"
                  v-tooltip.top="'Remove Rain'"
                  @click="removePlayerFromCampaign(slotProps.data)"
                  class="hover:bg-red-50 rounded-full"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import { Button } from "primevue";

import Filters from "../table/Filters.vue";
import { getFetch, deleteRequest } from "@/api/axiosClient.js";
import Swal from "sweetalert2";
import Loader from "../Loader.vue";

export default {
  name: "RainPlayerActive",
  props: {
    campaignId: { type: String, required: true },
    skinFilt: { required: true },
  },
  components: {
    DataTable,
    Column,
    Button,
    Filters,
    Loader,
  },
  data() {
    return {
      currentPage: 1,
      totalElements: 0,
      selectedLimit: 15,
      tableData: [],
      isLoading: false,
      skin_id: 0,
      player_id: "",
      freebet_id: null,

      // Definiamo i set di colonne come da logica originale
      base_columns: [
        { slug: "created_at", label: "Start Time", type: "date" },
        { slug: "end_at", label: "End Time", type: "date" },
      ],
      player_column: [
        { slug: "player_id", label: "Player ID", type: "text" },
        { slug: "freebet_id", label: "Freebet ID", type: "text" },
      ],
      advanced_column: [
        { slug: "quantity", label: "Quantity", type: "text" },
        { slug: "amount", label: "Amount", type: "number" },
        { slug: "counter", label: "Counter", type: "text" },
        { slug: "active", label: "Active", type: "boolean" },
        { slug: "is_closed", label: "Closed", type: "boolean" },
      ],
      action_column: [
        { type: "action_button", label: "Action", slug: "actions" },
      ],

      options: [
        {
          label: "Player ID",
          type: "text",
          placeholder: "Search...",
          property: "player_id",
        },
        {
          label: "Freebet ID",
          type: "text",
          placeholder: "Search...",
          property: "freebet_id",
        },
        {
          label: "Campaign ID",
          type: "text",
          property: "campaignId",
          disabled: true,
        },
      ],
    };
  },
  computed: {
    activeColumns() {
      return [
        ...this.player_column,
        ...this.base_columns,
        ...this.advanced_column,
        ...this.action_column,
      ];
    },
  },
  async mounted() {
    this.skin_id = this.skinFilt;
    await this.search();
  },
  methods: {
    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleString();
    },

    onPage(event) {
      this.currentPage = event.page + 1;
      this.selectedLimit = event.rows;
      this.search();
    },

    async resetPagination() {
      this.currentPage = 1;
    },

    async resetFilter() {
      this.currentPage = 1;
      this.selectedLimit = 15;
      this.player_id = "";
      this.freebet_id = null;
      await this.search();
    },

    async removePlayerFromCampaign(row) {
      const playerID = row.player_id;
      const campaignID = row.campaign_id || this.campaignId;

      const result = await Swal.fire({
        title: "Are you sure?",
        html: `Remove player <b class="text-orange-500">${playerID}</b> from campaign <b class="text-orange-500">${campaignID}</b>?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#10b981",
        cancelButtonColor: "#ef4444",
        confirmButtonText: "Yes, remove it!",
      });

      if (result.isConfirmed) {
        try {
          this.isLoading = true;
          await deleteRequest(`freebets/player/${campaignID}`, {
            player_id: playerID,
          });
          await Swal.fire(
            "Deleted!",
            "The player has been removed.",
            "success",
          );
          await this.search(); // Refresh invece di reload totale
        } catch (error) {
          Swal.fire("Error", "Failed to remove the player.", "error");
        } finally {
          this.isLoading = false;
        }
      }
    },

    async search() {
      this.isLoading = true;
      try {
        const params = {
          skin_id: this.skin_id,
          page: this.currentPage,
          limit: this.selectedLimit,
          campaign_id: this.campaignId,
        };

        if (this.player_id) params.player_id = this.player_id.trim();
        if (this.freebet_id) params.freebet_id = this.freebet_id.trim();

        const data = await getFetch(`freebets/players`, params);

        this.tableData = data.data;
        this.totalElements = data.totalItems;

        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        console.error("Fetch Error:", error);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Transizione fluida */
.animate-fade-in {
  animation: fadeIn 0.3s ease-in-out;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
